import type { SnowpackState } from '../sim/scene.ts';
import {
  ASPECT_SECTORS,
  WEAK_LAYER_DEFAULTS,
  type WeakLayerGrain,
  type WeakLayerPlacement,
  type WeakLayerSpec,
} from '../sim/weaklayers.ts';
import { conditions, snowpack } from '../state/store.ts';
import { formatNumber } from './format.ts';
import { NumberField } from './NumberField.tsx';

const PLACEMENT_LABELS: Record<WeakLayerPlacement, string> = {
  surface: 'On the old surface (buried by later snow)',
  depth: 'At a depth in the existing snow',
  ground: 'At the ground',
};

function updateLayers(next: WeakLayerSpec[]): void {
  conditions.value = { ...conditions.value, weakLayers: next };
}

function updateLayer(id: string, patch: Partial<WeakLayerSpec>): void {
  updateLayers(conditions.value.weakLayers.map((w) => (w.id === id ? { ...w, ...patch } : w)));
}

function addLayer(): void {
  const grain: WeakLayerGrain = 'FC';
  const layer: WeakLayerSpec = {
    id: crypto.randomUUID(),
    enabled: true,
    grain,
    placement: 'surface',
    depthCm: 30,
    thicknessCm: WEAK_LAYER_DEFAULTS[grain].thicknessCm,
    minElevation: 0,
    maxElevation: 2500,
    aspects: new Array(8).fill(true) as boolean[],
  };
  updateLayers([...conditions.value.weakLayers, layer]);
}

function removeLayer(id: string): void {
  updateLayers(conditions.value.weakLayers.filter((w) => w.id !== id));
}

/** Number of snowpack cells with any snow — the denominator for "placed in N cells (X %)". */
function snowCoveredCells(sp: SnowpackState): number {
  let n = 0;
  for (const c of sp.columns.count) if (c > 0) n++;
  return n;
}

const WEDGE_RADIUS = 38;
const CENTER = 42;

function wedgePath(startDeg: number, endDeg: number): string {
  const toXY = (deg: number): [number, number] => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [CENTER + WEDGE_RADIUS * Math.cos(rad), CENTER + WEDGE_RADIUS * Math.sin(rad)];
  };
  const [x1, y1] = toXY(startDeg);
  const [x2, y2] = toXY(endDeg);
  return `M ${CENTER} ${CENTER} L ${x1} ${y1} A ${WEDGE_RADIUS} ${WEDGE_RADIUS} 0 0 1 ${x2} ${y2} Z`;
}

function labelPos(midDeg: number): [number, number] {
  const rad = ((midDeg - 90) * Math.PI) / 180;
  const r = WEDGE_RADIUS * 0.65;
  return [CENTER + r * Math.cos(rad), CENTER + r * Math.sin(rad)];
}

interface AspectRoseProps {
  aspects: boolean[];
  onChange: (aspects: boolean[]) => void;
}

/** 8-sector clickable aspect rose (multi-select), matching ASPECT_SECTORS order (N, NE, E, …). */
function AspectRose({ aspects, onChange }: AspectRoseProps) {
  function toggle(i: number): void {
    const next = aspects.slice();
    next[i] = !next[i];
    onChange(next);
  }
  return (
    <div class="aspect-rose">
      <svg width={CENTER * 2} height={CENTER * 2} class="aspect-rose-svg">
        {ASPECT_SECTORS.map((label, i) => {
          const start = i * 45 - 22.5;
          const end = i * 45 + 22.5;
          const [lx, ly] = labelPos(i * 45);
          return (
            <g key={label}>
              <path
                d={wedgePath(start, end)}
                class={'aspect-wedge' + (aspects[i] ? ' active' : '')}
                onClick={() => toggle(i)}
              />
              <text x={lx} y={ly} class="aspect-wedge-label">
                {label}
              </text>
            </g>
          );
        })}
      </svg>
      <div class="aspect-rose-actions">
        <button type="button" onClick={() => onChange(new Array(8).fill(true) as boolean[])}>
          All
        </button>
        <button type="button" onClick={() => onChange(new Array(8).fill(false) as boolean[])}>
          None
        </button>
      </div>
    </div>
  );
}

interface WeakLayerRowProps {
  layer: WeakLayerSpec;
  placedCells: number;
  coveredCells: number;
}

function WeakLayerRow({ layer, placedCells, coveredCells }: WeakLayerRowProps) {
  const pct = coveredCells > 0 ? (placedCells / coveredCells) * 100 : 0;
  return (
    <div class="weak-layer-row">
      <div class="field-row weak-layer-row-header">
        <label class="checkbox-field">
          <input
            type="checkbox"
            checked={layer.enabled}
            onChange={(e) =>
              updateLayer(layer.id, { enabled: (e.target as HTMLInputElement).checked })
            }
          />
          Enabled
        </label>
        <button type="button" class="icon-button" onClick={() => removeLayer(layer.id)}>
          Remove
        </button>
      </div>

      <div class="field-row">
        <span class="field-label">Type</span>
        <select
          value={layer.grain}
          onChange={(e) => {
            const grain = (e.target as HTMLSelectElement).value as WeakLayerGrain;
            updateLayer(layer.id, { grain, thicknessCm: WEAK_LAYER_DEFAULTS[grain].thicknessCm });
          }}
        >
          {(Object.keys(WEAK_LAYER_DEFAULTS) as WeakLayerGrain[]).map((g) => (
            <option key={g} value={g}>
              {WEAK_LAYER_DEFAULTS[g].label}
            </option>
          ))}
        </select>
      </div>

      <div class="field-row">
        <span class="field-label">Placement</span>
        <select
          value={layer.placement}
          onChange={(e) =>
            updateLayer(layer.id, {
              placement: (e.target as HTMLSelectElement).value as WeakLayerPlacement,
            })
          }
        >
          {(Object.keys(PLACEMENT_LABELS) as WeakLayerPlacement[]).map((p) => (
            <option key={p} value={p}>
              {PLACEMENT_LABELS[p]}
            </option>
          ))}
        </select>
      </div>

      {layer.placement === 'depth' ? (
        <NumberField
          label="Depth"
          value={layer.depthCm}
          min={0}
          max={500}
          step={1}
          unit="cm"
          onChange={(v) => updateLayer(layer.id, { depthCm: v })}
        />
      ) : null}

      <NumberField
        label="Thickness"
        value={layer.thicknessCm}
        min={0.5}
        max={100}
        step={0.5}
        unit="cm"
        decimals={1}
        onChange={(v) => updateLayer(layer.id, { thicknessCm: v })}
      />

      <NumberField
        label="Min elevation"
        value={layer.minElevation}
        min={0}
        max={2500}
        step={10}
        unit="m"
        onChange={(v) => updateLayer(layer.id, { minElevation: v })}
      />
      <NumberField
        label="Max elevation"
        value={layer.maxElevation}
        min={0}
        max={2500}
        step={10}
        unit="m"
        onChange={(v) => updateLayer(layer.id, { maxElevation: v })}
      />

      <div class="field-row">
        <span class="field-label">Aspects</span>
      </div>
      <AspectRose aspects={layer.aspects} onChange={(a) => updateLayer(layer.id, { aspects: a })} />

      <p class="hint">
        Placed in {formatNumber(placedCells)} cells ({formatNumber(pct, 1)}% of snow-covered area)
      </p>
    </div>
  );
}

/** Weak-layer scenario events: list of user-defined weak layers, each with its type, how it's
 * placed relative to the storm, an elevation band and an aspect rose. Sits between "Existing
 * snow" and "Storm" in the panel, matching the scenario's time order. */
export function WeakLayersPanel() {
  const layers = conditions.value.weakLayers;
  const sp = snowpack.value;
  const covered = sp ? snowCoveredCells(sp) : 0;

  return (
    <section class="panel-section">
      <h2>Weak layers</h2>
      {layers.length === 0 ? <p class="hint">No weak layers defined.</p> : null}
      {layers.map((layer) => (
        <WeakLayerRow
          key={layer.id}
          layer={layer}
          placedCells={sp?.weakLayersPlaced[layer.id] ?? 0}
          coveredCells={covered}
        />
      ))}
      <button type="button" class="primary" onClick={addLayer}>
        Add weak layer
      </button>
    </section>
  );
}
